import React from 'react';
import { Tag, seg } from '../tags/Tag';
import type { ArrayCellProps } from './types';

/**
 * ArrayCell - Renders arrays/lists with tags and dropdowns
 * 
 * Modes:
 * - view: Display existing arrays with navigation ("3 Operations")
 * - select: Single selection from options
 * - multi-select: Multiple selection from options
 */
export const ArrayCell: React.FC<ArrayCellProps> = ({ value, config, row }) => {
  if (!config) {
    return <span className="table-cell-text">—</span>;
  }

  const mode = config.mode || 'view';
  const disabled = config.disabled;

  // VIEW MODE: Display existing arrays with navigation
  if (mode === 'view') {
    // Handle null/undefined/non-array values
    if (!value || !Array.isArray(value)) {
      return <span className="table-cell-text">—</span>;
    }

    const singularLabel = config.singularLabel || 'Item';
    const pluralLabel = config.pluralLabel || 'Items';
    const itemRenderer = config.itemRenderer || ((item) => String(item));
    const openable = config.openable;
    const onItemClick = config.onItemClick;
    const itemIcon = config.itemIcon;
    const footerAction = config.footerAction;

    // Handle empty arrays
    if (value.length === 0) {
      const hasFooterAction = footerAction && row;

      return (
        <Tag
          template="action-md"
          flush="left"
          segments={[seg.text('None', { size: 'lg' })]}
          visualOnly={!hasFooterAction}
          onClick={hasFooterAction ? () => footerAction.onClick(row) : undefined}
          disabled={disabled}
        />
      );
    }

    // Generate display text
    const displayText = `${value.length} ${value.length === 1 ? singularLabel : pluralLabel}`;

    // Convert array items to dropdown format
    const dropdownOptions = value.map((item, index) => {
      const isOpenable = typeof openable === 'function' ? openable(item) : openable;
      
      return {
        id: `item-${index}`,
        label: itemRenderer(item),
        openable: isOpenable,
        onOpen: isOpenable && onItemClick ? () => onItemClick(item) : undefined,
        icon: itemIcon ? itemIcon(item) : undefined
      };
    });

    const hasOpenableItems = dropdownOptions.some(opt => opt.openable && opt.onOpen);

    return (
      <Tag
        template="action-md"
        flush="left"
        segments={[seg.text(displayText, { size: 'lg' })]}
        dropdown="option"
        dropdownOptions={dropdownOptions}
        dropdownVariant="default"
        dropdownMaxHeight="12rem"
        disabled={disabled}
      />
    );
  }

  // SELECT MODE: Single selection from options
  if (mode === 'select') {
    const options = config.options || [];
    const onChange = config.onChange;
    const placeholder = config.placeholder || 'Select...';

    return (
      <Tag
        template="action-md"
        flush="left"
        segments={[seg.text(value || placeholder, { size: 'lg' })]}
        dropdown="option"
        dropdownOptions={options.map((opt: any) => typeof opt === 'string' ? { id: opt, label: opt } : { id: opt.value || opt.id, label: opt.title || opt.label })}
        onSelectionChange={onChange}
        disabled={disabled}
        dropdownVariant="default"
        dropdownMaxHeight="12rem"
      />
    );
  }

  // MULTI-SELECT MODE: Multiple selection from options
  if (mode === 'multi-select') {
    const options = config.options || [];
    const onMultiChange = config.onMultiChange;
    const selectedValues = Array.isArray(value) ? value : [];
    const displayText = selectedValues.length === 0 
      ? config.placeholder || 'None selected'
      : `${selectedValues.length} Selected`;

    // Convert to multi-select dropdown options
    const dropdownOptions = options.map(opt => ({
      id: opt,
      label: opt,
      selected: selectedValues.includes(opt)
    }));

    return (
      <Tag
        template="action-md"
        flush="left"
        segments={[seg.text(displayText, { size: 'lg' })]}
        dropdown="option"
        dropdownOptions={dropdownOptions}
        onSelectionChange={(value) => {
          // Toggle selection
          const newSelection = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
          onMultiChange?.(newSelection);
        }}
        disabled={disabled}
        dropdownVariant="default"
        dropdownMaxHeight="12rem"
      />
    );
  }

  // Fallback for invalid mode
  return <span className="table-cell-text">—</span>;
};
